import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Inbox } from 'lucide-react';
import { PageHeader } from '@/app/layout/AppShell';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { TaskRow } from '@/components/shared/TaskRow';
import { useUsers } from '@/features/settings/api';
import { useAuth } from '@/features/auth';
import { useInbox } from './api';

/** Everything needing *my* response, grouped by why it needs it (§5-F11). */
export function InboxPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: tasks, isLoading } = useInbox();
  const { data: people = [] } = useUsers();

  const peopleById = useMemo(
    () => Object.fromEntries(people.map((p) => [p._id, { name: p.name, avatarUrl: p.avatarUrl }])),
    [people],
  );

  const groups = useMemo(() => {
    const assigned = [];
    const toReview = [];
    const watching = [];
    for (const task of tasks ?? []) {
      if (task.reviewerId === user?._id) toReview.push(task);
      else if (task.assigneeIds.includes(user?._id ?? '')) assigned.push(task);
      else watching.push(task);
    }
    return [
      { key: 'review', label: 'Waiting on your review', tasks: toReview },
      { key: 'assigned', label: 'Assigned to you', tasks: assigned },
      { key: 'watching', label: 'You are watching', tasks: watching },
    ].filter((g) => g.tasks.length > 0);
  }, [tasks, user]);

  if (isLoading) {
    return (
      <>
        <PageHeader title="Inbox" />
        <div className="flex flex-col gap-2 p-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-9" />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Inbox"
        subtitle={
          groups.length === 0
            ? 'Nothing is waiting on you.'
            : `${tasks?.length ?? 0} items awaiting your response`
        }
      />

      <div className="max-w-[900px] p-4">
        {groups.length === 0 ? (
          <EmptyState
            icon={<Inbox className="size-6" />}
            title="Inbox zero"
            description="Nothing is assigned to you, waiting on your review, or moving on something you watch."
          />
        ) : (
          groups.map((group) => (
            <section key={group.key} className="mb-4">
              <h2 className="mb-1.5 text-[12px] font-medium uppercase tracking-wide vt-muted">
                {group.label} <span className="vt-mono">{group.tasks.length}</span>
              </h2>
              <div className="vt-surface overflow-hidden">
                {group.tasks.map((task) => (
                  <TaskRow
                    key={task._id}
                    task={task}
                    people={peopleById}
                    onOpen={() => navigate(`/tasks/${task.key}`)}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </>
  );
}
